import React from 'react';
import { useForm } from 'react-hook-form';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { TextBoxController } from '@components-clean/core/text-box';
import { useUnsavedChangesTracking, useUnsavedChangesConfirmation } from '@hooks-clean/index';
import { isValidTagLabel } from '@utils-clean/validation';
import { useTranslation } from '@hooks/index';
import { ShowError, ShowSuccess } from '@utils/notification';
import { TagAPI } from '@apis/bff-api/tag';

type AddTagDialogProps = {
    studyRef: string;
    timestamp?: string;
    onClose: () => void;
    onSubmit: () => void;
    existingTags: Tag[];
};

interface Tag {
    label: string;
    active: boolean;
}

const COMPONENT_NAME = 'add-tag-dialog';

const AddTagDialog = ({ studyRef, onClose, timestamp, onSubmit, existingTags }: AddTagDialogProps) => {
    const { t } = useTranslation();
    const { t: mtkTranslation }: { t: (key: string) => string } = useTranslation('sm', {
        keyPrefix: 'monitoringToolkit',
    });
    const { trigger: postTag, isMutating: isSaving } = TagAPI.usePostTag(studyRef, timestamp);

    const {
        control: textControl,
        handleSubmit,
        formState: { isDirty, isValid },
        watch,
    } = useForm<INewTagForm>({
        mode: 'all',
    });

    useUnsavedChangesTracking({
        area: 'subject-monitoring-toolkit',
        componentName: COMPONENT_NAME,
        hasChanges: isDirty,
    });
    const confirmLossOfChanges = useUnsavedChangesConfirmation({
        area: 'subject-monitoring-toolkit',
        mode: 'individual-components',
        componentNames: COMPONENT_NAME,
    });

    const onCloseHandler = async () => {
        const confirmed = await confirmLossOfChanges();
        if (!confirmed) {
            return;
        }

        onClose();
    };

    interface INewTagForm {
        tagLabel: string;
    }

    const onlySpacesRegex = /^\s+$/gm;
    const labelIsOnlySpaces = onlySpacesRegex.test(watch('tagLabel'));

    return (
        <DraggableDialog
            dataTestId="create-tag-dialog"
            onClose={onCloseHandler}
            onSubmit={() =>
                handleSubmit((values: INewTagForm) => {
                    if (!values.tagLabel) {
                        return;
                    }
                    postTag({ label: values.tagLabel })
                        .then(() => {
                            ShowSuccess(t('actionsSuccessfulToast'));
                            onSubmit();
                        })
                        .catch(() => {
                            ShowError(t('errorMessage'));
                            onClose();
                        });
                })()
            }
            title={mtkTranslation('addTagsModal.title')}
            primaryBtnDisabled={!isDirty || !isValid || labelIsOnlySpaces}
            primaryBtnLabel="Save"
            height={160}
            width={320}
        >
            <TextBoxController
                control={textControl}
                name="tagLabel"
                data-testid="new-tag-label"
                label={mtkTranslation('addTagsModal.inputLabel')}
                disabled={isSaving}
                maxLength={30}
                required
                validateRules={{
                    noInvalidCharacters: (value) => {
                        if (value && !isValidTagLabel(value as string))
                            return mtkTranslation('addTagsModal.invalidChars');
                        return true;
                    },
                    checkExistingTags: (value) => {
                        if (value) {
                            const trimmed = (value as string).trim().toLowerCase();
                            if (existingTags.some((tag) => tag.label.toLowerCase() === trimmed && tag.active)) {
                                return mtkTranslation('addTagsModal.tagAlreadyExists');
                            }
                            if (existingTags.some((tag) => tag.label.toLowerCase() === trimmed && !tag.active)) {
                                return mtkTranslation('addTagsModal.existingTagInactive');
                            }
                        }
                        return true;
                    },
                }}
            />
        </DraggableDialog>
    );
};

export default AddTagDialog;
